import type { ProviderCredentialCloudSyncInput, ProviderKind } from "@cmmd-forge/contracts";
import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { cmmdApiClient } from "../environments/cmmdApi";
import type { CmmdClaudeCredentialState, CmmdCodexCredentialState } from "../environments/cmmdApi";
import { updateForgeProviderConnectionState } from "../cmmd/forgeProviderAccess";
import {
  runCloudProviderSignIn,
  type CloudProviderSignInPhase,
} from "../environments/runtime/cloudProviderSignIn";

export const providerAccountsQueryKey = ["cmmd", "providers", "accounts"] as const;

/**
 * Card 1359 — client-side gate for the cloud-only sign-in affordance. The server
 * resource is 404 unless CMMD enables the feature; this keeps the UI from
 * offering a flow that would fail. Off unless the build sets the flag.
 */
export function isCloudProviderSignInEnabled(): boolean {
  return import.meta.env.VITE_CMMD_FORGE_AUTH_RUNTIME_ENABLED === "true";
}

/**
 * Card 1359 (Claude) — a SEPARATE gate for the personal-Claude cloud connect,
 * deliberately NOT reusing the codex flag above. Personal Claude puts a live
 * credential inside a runtime (the delivery is gated server-side by
 * CMMD_FORGE_PERSONAL_CLAUDE_IN_VM_ENABLED and needs CMMD-team sign-off), so the
 * connect UI must stay dark until that path is enabled — otherwise a user could
 * connect and it would harvest but never be consumed. Off unless the build sets
 * this flag; flip it together with the server flag when going live.
 */
export function isClaudeCloudSignInEnabled(): boolean {
  return import.meta.env.VITE_CMMD_FORGE_PERSONAL_CLAUDE_ENABLED === "true";
}

export interface CloudProviderSignInController<
  TCredential extends CmmdCodexCredentialState | CmmdClaudeCredentialState =
    CmmdCodexCredentialState,
> {
  readonly phase: CloudProviderSignInPhase | null;
  readonly busy: boolean;
  /**
   * The persistent connected-state read from the user's CMMD store (survives
   * reopening settings, unlike `phase`). `null` while the first read is in
   * flight or if it could not be resolved.
   */
  readonly connection: TCredential | null;
  readonly connectionLoading: boolean;
  readonly disconnecting: boolean;
  readonly start: (
    provider: ProviderKind,
    options?: { readonly credentialTarget?: ProviderCredentialCloudSyncInput["target"] },
  ) => void;
  readonly cancel: () => void;
  readonly disconnect: () => Promise<void>;
}

/**
 * React binding over `runCloudProviderSignIn`: exposes the live phase for the
 * surface to render (provisioning → connecting → device URL/code → done/error)
 * and a cancel that aborts + tears down. Aborts on unmount so a navigated-away
 * flow still cleans up its VM.
 */
type PersonalCredentialStateFor<P extends "codex" | "claudeAgent"> = P extends "claudeAgent"
  ? CmmdClaudeCredentialState
  : CmmdCodexCredentialState;

export function useCloudProviderSignIn<P extends "codex" | "claudeAgent" = "codex">(options?: {
  readonly enabled?: boolean;
  readonly provider?: P;
}): CloudProviderSignInController<PersonalCredentialStateFor<P>> {
  // When disabled (e.g. an execution-plane surface that runs `codex login`
  // locally), skip the store read entirely — the flow is never offered there.
  const enabled = options?.enabled ?? true;
  // Which provider's user-store credential this instance manages. Defaults to
  // codex for existing callers; claudeAgent selects the Claude store.
  const provider = options?.provider ?? "codex";
  const [phase, setPhase] = useState<CloudProviderSignInPhase | null>(null);
  const [busy, setBusy] = useState(false);
  const [connection, setConnection] = useState<
    CmmdCodexCredentialState | CmmdClaudeCredentialState | null
  >(null);
  const [connectionLoading, setConnectionLoading] = useState(enabled);
  const [disconnecting, setDisconnecting] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const mountedRef = useRef(true);
  const queryClient = useQueryClient();

  const refreshConnection = useCallback(async () => {
    setConnectionLoading(true);
    try {
      const state =
        provider === "claudeAgent"
          ? await cmmdApiClient.getUserClaudeCredential()
          : await cmmdApiClient.getUserCodexCredential();
      if (mountedRef.current) setConnection(state);
    } catch {
      // Unknown (network / feature-disabled): fall back to the connect flow
      // rather than misreporting a connection.
      if (mountedRef.current) setConnection({ connected: false });
    } finally {
      if (mountedRef.current) setConnectionLoading(false);
    }
  }, [provider]);

  useEffect(() => {
    mountedRef.current = true;
    if (enabled) {
      void refreshConnection();
    } else {
      setConnectionLoading(false);
    }
    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
    };
  }, [refreshConnection, enabled]);

  const start = useCallback(
    (
      provider: ProviderKind,
      options?: { readonly credentialTarget?: ProviderCredentialCloudSyncInput["target"] },
    ) => {
      if (!enabled) return;
      if (abortRef.current) return; // a run is already in flight
      const controller = new AbortController();
      abortRef.current = controller;
      setPhase(null);
      setBusy(true);
      void runCloudProviderSignIn({
        provider,
        credentialTarget: options?.credentialTarget,
        signal: controller.signal,
        onPhase: (next) => {
          if (mountedRef.current) setPhase(next);
        },
      })
        .then(async (result) => {
          // A successful harvest wrote the credential to the user store — reflect
          // it as a persistent connection.
          if (result.status === "done" && isPersonalProvider(provider)) {
            await updateForgeProviderConnectionState({
              queryClient,
              provider,
              connected: true,
            });
            await queryClient.invalidateQueries({
              queryKey: providerAccountsQueryKey,
              refetchType: "active",
            });
            await refreshConnection();
            await queryClient.refetchQueries({
              queryKey: providerAccountsQueryKey,
              type: "active",
            });
          }
        })
        .catch(() => undefined)
        .finally(() => {
          abortRef.current = null;
          if (mountedRef.current) setBusy(false);
        });
    },
    [enabled, queryClient, refreshConnection],
  );

  const cancel = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const disconnect = useCallback(async () => {
    if (disconnecting) return;
    setDisconnecting(true);
    const request =
      provider === "claudeAgent"
        ? cmmdApiClient.disconnectUserClaudeCredential()
        : cmmdApiClient.disconnectUserCodexCredential();
    try {
      const state = await request;
      if (isPersonalProvider(provider)) {
        await updateForgeProviderConnectionState({
          queryClient,
          provider,
          connected: false,
        });
      }
      await queryClient.invalidateQueries({ queryKey: providerAccountsQueryKey });
      if (!mountedRef.current) return;
      setConnection(state);
      setPhase(null); // back to the connect affordance
    } finally {
      if (mountedRef.current) setDisconnecting(false);
    }
  }, [disconnecting, provider, queryClient]);

  return {
    phase,
    busy,
    connection: connection as PersonalCredentialStateFor<P> | null,
    connectionLoading,
    disconnecting,
    start,
    cancel,
    disconnect,
  };
}

function isPersonalProvider(
  provider: ProviderKind,
): provider is Extract<ProviderKind, "codex" | "claudeAgent"> {
  return provider === "codex" || provider === "claudeAgent";
}
